import { Trans, useLingui } from '@lingui/react/macro'
import { useGameplayHistoryInfiniteQuery } from '@/features/gameplay-history/use-gameplay-history-infinite-query/use-gameplay-history-infinite-query'
import { useMemo, useState } from 'react'
import { HistoryFilters } from '@/features/history/history-filters/history-filters'
import {
  GameplayFiltersContent,
  GameplayFiltersValues,
} from '@/features/gameplay-history/gameplay-filters/gameplay-filters'
import { GameplayHistoryContent } from '@/features/gameplay-history/gameplay-history-content'
import { GameplayHistoryCardProps } from '@/features/gameplay-history/gameplay-history-card/gameplay-history-card'
import { PlayerSelfRoundInfoEntry } from '@/apis/gam-game-round-info'
import { getFiltersQueryPayload } from '@/features/gameplay-history/gameplay-filters/gameplay-filters.utils'
import { createFileRoute } from '@tanstack/react-router'
import { assertCanPlayBeforeLoad } from '@/features/ctrls-and-caps/assert-can-play-before-load'
import { TertiaryPageLayout } from '@/routes/-tertiary-page-layout'

const countAppliedFilters = (filters: GameplayFiltersValues | null) => {
  let count = 0

  if (filters?.to && filters?.from) {
    count += 1
  }

  const isCoinsFiltered =
    Array.isArray(filters?.coinType) && filters.coinType.length < 2

  if (isCoinsFiltered) {
    count += 1
  }

  return count
}

type GameplaysMap = Record<string, GameplayHistoryCardProps[]>

export const Route = createFileRoute('/_app/your-play/gameplay-history')({
  component: GameplayHistory,
  beforeLoad: async ({ context }) => {
    return assertCanPlayBeforeLoad(context.queryClient)
  },
})

function GameplayHistory() {
  const [filters, setFilters] = useState<GameplayFiltersValues | null>(null)
  const appliedFiltersCount = countAppliedFilters(filters)
  const { i18n, t } = useLingui()
  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGameplayHistoryInfiniteQuery({
    query: getFiltersQueryPayload(filters),
  })

  const gameplays = useMemo(() => {
    const gameplaysMap: GameplaysMap =
      data?.pages
        ?.flatMap((page) => page.rounds)
        .reduce((acc: GameplaysMap, gameplay: PlayerSelfRoundInfoEntry) => {
          const item: GameplayHistoryCardProps = {
            id: gameplay.gameRoundId || '-',
            won: gameplay.tokensWon || 0,
            played: gameplay.tokensPlayed || 0,
            date: new Date(gameplay.timestamp || ''),
            name: gameplay.gameName || '-',
            tokenType: gameplay.tokenType || '-',
          }

          const playDate = new Date(
            gameplay.timestamp || '',
          ).toLocaleDateString(i18n.locale, {
            dateStyle: 'medium',
          })

          if (!acc[playDate]) {
            acc[playDate] = []
          }
          acc[playDate].push(item)

          return acc
        }, {}) || {}

    return Object.keys(gameplaysMap).map((key) => ({
      title: key,
      items: gameplaysMap[key],
    }))
  }, [data, i18n.locale])

  const isHeaderContentVisible = filters
    ? true
    : !isLoading && !isError && gameplays.length > 0

  return (
    <TertiaryPageLayout headerText={t`Gameplay History`}>
      <header className="flex flex-col">
        {isHeaderContentVisible ? (
          <div
            className="my-2 flex items-center justify-between px-4 font-family-display leading-6 font-medium text-content"
            data-testid="gameplay-history-header-content"
          >
            <Trans>All Gameplays</Trans>
            <HistoryFilters
              filters={filters}
              filtersCount={appliedFiltersCount}
              title={<Trans>Purchase Filters</Trans>}
              description={
                <Trans>2 weeks of history is available to filter</Trans>
              }
              content={
                <GameplayFiltersContent
                  filters={filters}
                  onShowAllResults={setFilters}
                />
              }
              classes={{ content: 'p-0! lg:w-120 xl:w-120' }}
            />
          </div>
        ) : null}
      </header>
      <GameplayHistoryContent
        gameplays={gameplays}
        isError={isError}
        isLoading={isLoading}
        filters={filters}
        refetch={refetch}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
      />
    </TertiaryPageLayout>
  )
}
